/**
 * API Request/Response Schemas
 *
 * Zod schemas for type-safe API contracts and client-side validation.
 * These match the Go backend domain models.
 */

import { z } from "zod";

// ============================================================================
// Auth Schemas
// ============================================================================

export const SignupLinkRequestSchema = z.object({
  email: z.string().email("Invalid email address"),
  fullName: z.string().min(2, "Name must be at least 2 characters"),
  redirectUrl: z.string().url().optional(),
});

export type SignupLinkRequest = z.infer<typeof SignupLinkRequestSchema>;

export const LoginLinkRequestSchema = z.object({
  email: z.string().email("Invalid email address"),
  redirectUrl: z.string().url().optional(),
});

export type LoginLinkRequest = z.infer<typeof LoginLinkRequestSchema>;

export const MagicLinkResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number(),
});

export type MagicLinkResponse = z.infer<typeof MagicLinkResponseSchema>;

export const RefreshTokenRequestSchema = z.object({
  refreshToken: z.string(),
});

export type RefreshTokenRequest = z.infer<typeof RefreshTokenRequestSchema>;

// ============================================================================
// Account/User Schemas
// ============================================================================

export const AccountSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  fullName: z.string(),
  role: z.enum(["buyer", "seller", "broker", "admin"]),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Account = z.infer<typeof AccountSchema>;

// ============================================================================
// Listing Schemas
// ============================================================================

export const ListingCreateRequestSchema = z.object({
  title: z.string().min(5, "Title must be at least 5 characters").max(200),
  description: z.string().min(20).max(5000),
  price: z.number().positive("Price must be positive"),
  currency: z.enum(["USD", "EUR", "GBP", "AUD"]),
  location: z.string().min(2),
  type: z.enum(["powerboat", "sailboat", "yacht", "catamaran", "other"]),
  year: z.number().int().min(1900).max(new Date().getFullYear()),
  length: z.number().positive("Length must be positive"),
  lengthUnit: z.enum(["feet", "meters"]),
  make: z.string().min(1),
  model: z.string().min(1),
  mediaUrls: z.array(z.string().url()).max(20),
});

export type ListingCreateRequest = z.infer<typeof ListingCreateRequestSchema>;

export const ListingSchema = z.object({
  id: z.string().uuid(),
  sellerId: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  price: z.number(),
  currency: z.string(),
  location: z.string(),
  type: z.string(),
  year: z.number(),
  length: z.number(),
  lengthUnit: z.string(),
  make: z.string(),
  model: z.string(),
  mediaUrls: z.array(z.string()),
  status: z.enum(["draft", "pending", "published", "suspended"]),
  views: z.number().default(0),
  inquiries: z.number().default(0),
  packageTier: z.enum(["base", "featured", "top"]).nullable(),
  packageExpiresAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Listing = z.infer<typeof ListingSchema>;

export const ListingDetailSchema = ListingSchema.extend({
  seller: AccountSchema.partial().nullable(),
  messages: z.array(
    z.object({
      id: z.string().uuid(),
      buyerId: z.string().uuid(),
      body: z.string(),
      status: z.enum(["pending", "delivered", "read"]),
      createdAt: z.string().datetime(),
    })
  ).default([]),
});

export type ListingDetail = z.infer<typeof ListingDetailSchema>;

// ============================================================================
// Message Schemas
// ============================================================================

export const MessageCreateRequestSchema = z.object({
  listingId: z.string().uuid(),
  body: z.string().min(1).max(2000),
  parentId: z.string().uuid().optional(),
});

export type MessageCreateRequest = z.infer<typeof MessageCreateRequestSchema>;

export const MessageSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  threadId: z.string().uuid(),
  buyerId: z.string().uuid(),
  buyer: AccountSchema.partial(),
  body: z.string(),
  status: z.enum(["pending", "delivered", "read"]),
  notifiedAt: z.string().datetime().nullable(),
  readAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
});

export type Message = z.infer<typeof MessageSchema>;

export const MessageThreadSchema = z.object({
  threadId: z.string().uuid(),
  listingId: z.string().uuid(),
  buyerId: z.string().uuid(),
  buyer: AccountSchema.partial(),
  lastMessage: MessageSchema,
  unreadCount: z.number().default(0),
  messages: z.array(MessageSchema),
});

export type MessageThread = z.infer<typeof MessageThreadSchema>;

// ============================================================================
// Billing/Checkout Schemas
// ============================================================================

export const CheckoutSessionRequestSchema = z.object({
  listingId: z.string().uuid(),
  packageTier: z.enum(["base", "featured", "top"]),
  quantity: z.number().int().positive().default(1),
});

export type CheckoutSessionRequest = z.infer<typeof CheckoutSessionRequestSchema>;

export const CheckoutSessionSchema = z.object({
  checkoutUrl: z.string().url(),
  sessionId: z.string(),
  expiresAt: z.string().datetime(),
});

export type CheckoutSession = z.infer<typeof CheckoutSessionSchema>;

// ============================================================================
// Admin/Moderation Schemas
// ============================================================================

export const ListingStatusUpdateSchema = z.object({
  status: z.enum(["pending", "published", "suspended"]),
  rejectionReason: z.string().optional(),
});

export type ListingStatusUpdate = z.infer<typeof ListingStatusUpdateSchema>;

export const AdminListingSchema = ListingSchema.extend({
  seller: AccountSchema.partial(),
  submittedAt: z.string().datetime(),
  reviewedAt: z.string().datetime().nullable(),
  reviewedBy: z.string().uuid().nullable(),
});

export type AdminListing = z.infer<typeof AdminListingSchema>;

// ============================================================================
// Search/Filter Schemas
// ============================================================================

export const ListingsFilterSchema = z.object({
  search: z.string().optional(),
  type: z.array(z.string()).optional(),
  minPrice: z.number().optional(),
  maxPrice: z.number().optional(),
  location: z.string().optional(),
  year: z.number().optional(),
  minLength: z.number().optional(),
  maxLength: z.number().optional(),
  packageTier: z.enum(["base", "featured", "top"]).optional(),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(20),
});

export type ListingsFilter = z.infer<typeof ListingsFilterSchema>;

export const PaginatedListingsSchema = z.object({
  listings: z.array(ListingSchema),
  nextCursor: z.string().nullable(),
  hasMore: z.boolean(),
});

export type PaginatedListings = z.infer<typeof PaginatedListingsSchema>;

// ============================================================================
// Scraping/Admin Schemas (future)
// ============================================================================

export const ScrapedSourceSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  url: z.string().url(),
  lastRun: z.string().datetime().nullable(),
  nextRun: z.string().datetime().nullable(),
  status: z.enum(["enabled", "disabled", "error"]),
  errorMessage: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export type ScrapedSource = z.infer<typeof ScrapedSourceSchema>;

export const ScrapedListingSchema = z.object({
  id: z.string().uuid(),
  externalId: z.string(),
  sourceId: z.string().uuid(),
  title: z.string(),
  price: z.number(),
  currency: z.string(),
  location: z.string(),
  url: z.string().url(),
  status: z.enum(["pending", "approved", "rejected", "archived"]),
  importedAt: z.string().datetime(),
  approvedAt: z.string().datetime().nullable(),
});

export type ScrapedListing = z.infer<typeof ScrapedListingSchema>;
